import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { userHasRole } from '../utils/roleHelpers';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Plus, Search, Filter, Eye, Edit2, Trash2,
  RefreshCw, X, Home, CheckCircle, AlertCircle, Power,
  Phone, Mail, MapPin, ToggleLeft, ToggleRight, Star, Loader2,
  Upload, Download, FileSpreadsheet, SlidersHorizontal,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import { propertiesAPI, getErrorMessage } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import PropertyAdvancedFilter from '../components/PropertyAdvancedFilter';
import GRBMapModal from '../components/GRBMapModal';
// RoomInventoryCharts kept but not rendered — can re-enable via conditional rendering
// import RoomInventoryCharts from '../components/RoomInventoryCharts';

// Star Rating Display
const StarDisplay = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(star => (
      <Star
        key={star}
        size={12}
        className={star <= (rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}
      />
    ))}
  </div>
);

// Property Type Badge
const TypeBadge = ({ type }) => {
  return type ? (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700 border border-purple-200">
      {type}
    </span>
  ) : null;
};

/**
 * SECTION 3: Toggle switch for Active/Inactive status
 */
const StatusToggle = ({ isActive, onChange, loading, disabled }) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      if (disabled) return;
      onChange();
    }}
    disabled={loading || disabled}
    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
      isActive 
        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
    } ${(loading || disabled) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    data-testid="status-toggle"
  >
    {loading ? (
      <Loader2 size={14} className="animate-spin" />
    ) : isActive ? (
      <ToggleRight size={16} />
    ) : (
      <ToggleLeft size={16} />
    )}
    {isActive ? 'Active' : 'Inactive'}
  </button>
);

// Property Card Component
const PropertyCard = ({ property, onView, onEdit, onDelete, onToggleStatus, onMapGRB, canDelete, canEdit, canMapGRB, isToggling }) => {
  const isActive = property.propertyStatus === 'Active';
  const grbId = property.grb_property_id || property.leadId; // leadId fallback shows the link even on legacy rows
  
  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group"
      data-testid={`property-card-${property.id}`}
    >
      {/* Card Header */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-sm">
              {property.propertyName?.charAt(0)?.toUpperCase() || 'P'}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 line-clamp-1">
                {property.propertyName || 'Unnamed Property'}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <StarDisplay rating={property.starRating} />
                <TypeBadge type={property.propertyType} />
              </div>
            </div>
          </div>
          <StatusToggle 
            isActive={isActive} 
            onChange={() => onToggleStatus(property)} 
            loading={isToggling === property.id}
            disabled={!canEdit}
          />
        </div>
      </div>
      
      {/* GRB ID badge — always visible (read-only for everyone) + a small
          "Map" affordance for CX/CX-Leader/Admin to set the GRB. */}
      <div className="px-5 pt-3 flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium ${
          grbId ? 'bg-teal-50 text-teal-700 border border-teal-100' : 'bg-gray-100 text-gray-500 border border-gray-200'
        }`} data-testid={`property-grb-${property.id}`}>
          <span className="opacity-70">GRB ID:</span>
          <span className="font-mono font-bold">{grbId || 'Not mapped'}</span>
        </span>
        {canMapGRB && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMapGRB(property); }}
            className="px-2 py-1 text-[11px] font-medium text-teal-700 border border-teal-200 rounded-md hover:bg-teal-50"
            data-testid={`property-map-grb-${property.id}`}
          >
            {grbId ? 'Update' : 'Map to account'}
          </button>
        )}
      </div>
      
      {/* Card Body */}
      <div className="p-5 space-y-3">
        {(property.address || property.city || property.state) && (
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <MapPin size={16} className="text-gray-400 mt-0.5 shrink-0" />
            <span className="line-clamp-2">
              {[property.address, property.city, property.state].filter(Boolean).join(', ')}
            </span>
          </div>
        )}
        {property.mobile && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Phone size={16} className="text-gray-400 shrink-0" />
            <span className="font-mono">{property.mobile}</span>
          </div>
        )}
        {property.email && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Mail size={16} className="text-gray-400 shrink-0" />
            <span className="truncate">{property.email}</span>
          </div>
        )}
        {property.ownerName && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="font-medium">Owner:</span>
            <span>{property.ownerName}</span>
          </div>
        )}
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Home size={16} className="text-gray-400" />
            <span>Total Rooms: <strong className="text-gray-700">{property.totalRooms || property.rooms?.reduce((sum, r) => sum + (r.count || 0), 0) || 0}</strong></span>
          </div>
          {property.rooms?.length > 0 && (
            <span>Room Types: <strong className="text-gray-700">{property.rooms.length}</strong></span>
          )}
        </div>
      </div>
      
      {/* Card Footer */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {property.createdAt && new Date(property.createdAt).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onView(property.id)}
            className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
            title="View Details"
            data-testid={`view-property-${property.id}`}
          >
            <Eye size={16} />
          </button>
          {canEdit && (
            <button
              onClick={() => onEdit(property.id)}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit"
              data-testid={`edit-property-${property.id}`}
            >
              <Edit2 size={16} />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(property)}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete (Admin only)"
              data-testid={`delete-property-${property.id}`}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ title, count, icon: Icon, color, onClick, isActive }) => (
  <div 
    onClick={onClick}
    className={`bg-white rounded-xl p-5 border-2 transition-all cursor-pointer hover:shadow-md ${
      isActive ? `border-${color}-500 shadow-md` : 'border-transparent hover:border-gray-200'
    }`}
    data-testid={`summary-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{count}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl bg-${color}-50 flex items-center justify-center`}>
        <Icon size={24} className={`text-${color}-500`} />
      </div>
    </div>
  </div>
);

// Delete Confirmation Modal
const DeleteModal = ({ isOpen, onClose, onConfirm, propertyName }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <AlertCircle className="text-orange-600" size={20} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Move to Recycle Bin</h3>
        </div>
        <p className="text-gray-600 mb-6">
          Move <span className="font-medium">"{propertyName}"</span> to the Recycle Bin? You can restore it later from Settings.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            data-testid="confirm-delete-btn"
          >
            Move to Recycle Bin
          </button>
        </div>
      </div>
    </div>
  );
};

// Active filter tag
const FilterTag = ({ label, value, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-xs font-medium" data-testid={`filter-tag-${label}`}>
    <span className="text-teal-500 font-normal">{label}:</span> {value}
    <button onClick={onRemove} className="ml-0.5 hover:text-teal-900"><X size={12} /></button>
  </span>
);

const Properties = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canDelete: canDeletePerm, canEdit: canEditPerm } = usePermissions();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, property: null });
  const [togglingStatus, setTogglingStatus] = useState(null);
  const [showImportExport, setShowImportExport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [manualMappings, setManualMappings] = useState({});
  const fileInputRef = useRef(null);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalProperties, setTotalProperties] = useState(0);
  const [summaryStats, setSummaryStats] = useState({ total: 0, active: 0, inactive: 0 });
  // Debounced search query — avoids re-issuing /properties/filter on every keystroke.
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);
  // Cancel-stale-request controller for /properties/filter
  const propsFilterAbortRef = useRef(null);
  // Room inventory charts (disabled — kept for future re-enable)
  // const [roomInventoryData, setRoomInventoryData] = useState({});
  // const [roomCrossFilters, setRoomCrossFilters] = useState({ roomType: null, roomDistribution: null });
  // const [roomChartLoading, setRoomChartLoading] = useState(false);

  const isAdmin = userHasRole(user, 'admin');
  // iter250 — Delete gating is now driven by the effective `properties.delete`
  // permission (which admins always have via full access) so an admin can
  // grant delete rights to any role via the User Management toggle grid
  // without a code change.
  const canDeleteProperty = isAdmin || canDeletePerm('properties');
  const userRole = (user?.role || '').toLowerCase();
  // iter250 — Edit/GRB-map gating migrated from the hardcoded view-only role
  // list (['gtm_sales','gtm_sales_leader','qc','cx_leader']) to the
  // effective `properties.edit` permission. Any role that the admin
  // grants `properties.edit` to will now see the Edit icon, the Save
  // button in the drawer, and be treated as non-view-only. Roles with
  // base `properties.edit` (cx, cx_leader, admin, executive) continue
  // to work exactly as before because their base permission matrix
  // includes edit. GRB-map continues to piggyback on edit access.
  const canEditProperty = isAdmin || canEditPerm('properties');
  const isViewOnlyRole = !canEditProperty;
  const isGtmSales = isViewOnlyRole; // keep var name for downstream JSX references
  const canMapGRB = canEditProperty; // CX, Admin, Executive (or any granted role) only
  // Phase 19.5 — Admin bulk-delete is now strictly Admin-only (no more
  // localStorage flag). Toolbar is hidden for every other role.
  const bulkDeleteEnabled = isAdmin;
  // Import/Export is restricted to Admin & Executive only.
  const canImportExport = isAdmin || userRole === 'executive';
  const [showBulkDeletePropsModal, setShowBulkDeletePropsModal] = useState(false);
  const [bulkDeletingProps, setBulkDeletingProps] = useState(false);
  const [grbMapTarget, setGrbMapTarget] = useState(null);
  // GRB Mapping quick filter: 'all' | 'mapped' | 'not_mapped'
  const [grbMappingFilter, setGrbMappingFilter] = useState('all');

  // Load filter options once
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const res = await propertiesAPI.getFilterOptions();
        setFilterOptions(res.data);
      } catch (err) {
        console.error('Error loading filter options:', err);
      }
    };
    loadFilterOptions();
  }, []);

  // Build filter payload combining quick search, status filter, and advanced filters
  const buildFilterPayload = useCallback(() => {
    const filters = { ...advancedFilters };
    if (debouncedSearchQuery) filters.propertyName = debouncedSearchQuery;
    if (statusFilter === 'active') filters.status = 'Active';
    else if (statusFilter === 'inactive') filters.status = 'Inactive';
    if (grbMappingFilter !== 'all') filters.grbMapping = grbMappingFilter;
    return filters;
  }, [advancedFilters, debouncedSearchQuery, statusFilter, grbMappingFilter]);

  // Load properties via server-side filtering (always uses /api/properties/filter)
  const loadProperties = useCallback(async () => {
    // Cancel any in-flight request from a previous filter/keystroke
    if (propsFilterAbortRef.current) {
      try { propsFilterAbortRef.current.abort(); } catch (_e) { /* ignore */ }
    }
    const controller = new AbortController();
    propsFilterAbortRef.current = controller;
    try {
      setLoading(true);
      const filters = buildFilterPayload();
      const res = await propertiesAPI.filterProperties(
        {
          filters,
          page: currentPage,
          limit: itemsPerPage === 'all' ? 10000 : itemsPerPage,
        },
        { signal: controller.signal }
      );
      // If a newer request superseded us, drop this response.
      if (propsFilterAbortRef.current !== controller) return;
      setProperties(res.data.properties || []);
      setTotalProperties(res.data.total || 0);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Error loading properties:', error);
    } finally {
      if (propsFilterAbortRef.current === controller) setLoading(false);
    }
  }, [buildFilterPayload, currentPage, itemsPerPage]);

  // Load summary stats via lightweight aggregation endpoint (single count query).
  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await api.get('/properties/stats');
        setSummaryStats({
          total: res.data?.total || 0,
          active: res.data?.active || 0,
          inactive: res.data?.inactive || 0,
        });
      } catch (err) {
        console.error('Error loading stats:', err);
      }
    };
    loadStats();
  }, []);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  // Reset page on filter change
  useEffect(() => { setCurrentPage(1); }, [advancedFilters, debouncedSearchQuery, statusFilter, grbMappingFilter, itemsPerPage]);

  // Room inventory chart loading (disabled — kept for future re-enable)
  // const loadRoomInventoryStats = useCallback(async () => { ... }, []);
  // useEffect(() => { loadRoomInventoryStats(); }, [loadRoomInventoryStats]);

  const totalPages = useMemo(() => {
    if (itemsPerPage === 'all') return 1;
    return Math.ceil(totalProperties / itemsPerPage) || 1;
  }, [totalProperties, itemsPerPage]);

  const handleView = (id) => navigate(`/properties/${id}`);
  const handleEdit = (id) => navigate(`/properties/${id}?mode=edit`);

  const handleToggleStatus = async (property) => {
    try {
      setTogglingStatus(property.id);
      const newStatus = property.propertyStatus === 'Active' ? 'Inactive' : 'Active';
      await propertiesAPI.update(property.id, { propertyStatus: newStatus });
      loadProperties();
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update property status');
    } finally {
      setTogglingStatus(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.property) return;
    try {
      await propertiesAPI.delete(deleteModal.property.id);
      loadProperties();
      setDeleteModal({ isOpen: false, property: null });
    } catch (error) {
      console.error('Error deleting property:', error);
      if (error.response?.status === 403) alert('Only Admin can delete properties');
    }
  };

  const handleBulkDeleteProperties = async () => {
    if (selectedPropertyIds.length === 0) return;
    // Phase 19.5 — proper confirmation modal (was window.confirm).
    setShowBulkDeletePropsModal(true);
  };

  const confirmBulkDeleteProperties = async () => {
    if (bulkDeletingProps || selectedPropertyIds.length === 0) return;
    setBulkDeletingProps(true);
    try {
      await api.post('/properties/delete-bulk', { ids: selectedPropertyIds });
      setSelectedPropertyIds([]);
      setShowBulkDeletePropsModal(false);
      loadProperties();
    } catch (err) {
      alert(getErrorMessage(err));
    } finally {
      setBulkDeletingProps(false);
    }
  };

  const togglePropertySelect = (id, checked) => {
    setSelectedPropertyIds(prev => checked ? [...prev, id] : prev.filter(i => i !== id));
  };

  const toggleAllProperties = (checked) => {
    setSelectedPropertyIds(checked ? properties.map(p => p.id) : []);
  };

  const handleAdvancedFilterApply = (filters) => {
    setAdvancedFilters(filters);
    setShowAdvancedFilter(false);
  };

  const removeAdvancedFilter = (key) => {
    setAdvancedFilters(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  // Build active filter tags for display
  const activeFilterTags = useMemo(() => {
    const tags = [];
    const f = advancedFilters;
    if (f.propertyName) tags.push({ key: 'propertyName', label: 'Name', value: f.propertyName });
    if (f.ownerName) tags.push({ key: 'ownerName', label: 'Owner', value: f.ownerName });
    if (f.propertyTypes?.length) tags.push({ key: 'propertyTypes', label: 'Type', value: f.propertyTypes.join(', ') });
    if (f.status) tags.push({ key: 'status', label: 'Status', value: f.status });
    if (f.cities?.length) tags.push({ key: 'cities', label: 'City', value: f.cities.length > 2 ? `${f.cities.length} cities` : f.cities.join(', ') });
    if (f.states?.length) tags.push({ key: 'states', label: 'State', value: f.states.length > 2 ? `${f.states.length} states` : f.states.join(', ') });
    if (f.pinCode) tags.push({ key: 'pinCode', label: 'Pin', value: f.pinCode });
    if (f.createdDateFrom || f.createdDateTo) tags.push({ key: 'createdDateFrom', label: 'Date', value: `${f.createdDateFrom || '...'} - ${f.createdDateTo || '...'}`, removeKeys: ['createdDateFrom', 'createdDateTo'] });
    if (f.totalRoomsMin || f.totalRoomsMax) tags.push({ key: 'totalRoomsMin', label: 'Rooms', value: `${f.totalRoomsMin || 0} - ${f.totalRoomsMax || 'any'}`, removeKeys: ['totalRoomsMin', 'totalRoomsMax'] });
    if (f.roomCategories?.length) tags.push({ key: 'roomCategories', label: 'Room Cat.', value: f.roomCategories.length > 2 ? `${f.roomCategories.length} types` : f.roomCategories.join(', ') });
    if (f.bedTypes?.length) tags.push({ key: 'bedTypes', label: 'Bed', value: f.bedTypes.join(', ') });
    if (f.acStatuses?.length) tags.push({ key: 'acStatuses', label: 'AC', value: f.acStatuses.join(', ') });
    if (f.amenities && Object.keys(f.amenities).length) {
      const entries = Object.entries(f.amenities);
      entries.forEach(([k, v]) => tags.push({ key: `amenity_${k}`, label: k, value: v === 'yes' ? 'Yes' : 'No', removeAmenity: k }));
    }
    if (f.facilities && Object.keys(f.facilities).length) {
      const entries = Object.entries(f.facilities);
      entries.forEach(([k, v]) => tags.push({ key: `facility_${k}`, label: k, value: v === 'yes' ? 'Yes' : 'No', removeFacility: k }));
    }
    if (f.bankName) tags.push({ key: 'bankName', label: 'Bank', value: f.bankName });
    if (f.ifscCode) tags.push({ key: 'ifscCode', label: 'IFSC', value: f.ifscCode });
    if (f.gstNumber) tags.push({ key: 'gstNumber', label: 'GST', value: f.gstNumber });
    if (f.hasBankDetails) tags.push({ key: 'hasBankDetails', label: 'Bank Info', value: f.hasBankDetails === 'yes' ? 'Yes' : 'No' });
    if (f.hasGST) tags.push({ key: 'hasGST', label: 'GST Avail.', value: f.hasGST === 'yes' ? 'Yes' : 'No' });
    return tags;
  }, [advancedFilters]);

  const handleRemoveTag = (tag) => {
    if (tag.removeKeys) {
      setAdvancedFilters(prev => {
        const updated = { ...prev };
        tag.removeKeys.forEach(k => delete updated[k]);
        return updated;
      });
    } else if (tag.removeAmenity) {
      setAdvancedFilters(prev => {
        const amenities = { ...prev.amenities };
        delete amenities[tag.removeAmenity];
        return { ...prev, amenities: Object.keys(amenities).length ? amenities : undefined };
      });
    } else if (tag.removeFacility) {
      setAdvancedFilters(prev => {
        const facilities = { ...prev.facilities };
        delete facilities[tag.removeFacility];
        return { ...prev, facilities: Object.keys(facilities).length ? facilities : undefined };
      });
    } else {
      removeAdvancedFilter(tag.key);
    }
  };

  const advancedFilterCount = activeFilterTags.length;

  // Property Export (Master Template)
  const handleExport = async (format = 'excel') => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/export?format=${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        let errorMsg = 'Export failed';
        try { const errData = await response.json(); errorMsg = errData.detail || errorMsg; } catch {}
        throw new Error(errorMsg);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `properties_export.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      alert(error.message || 'Failed to export properties');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/export/template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        let errorMsg = 'Template download failed';
        try { const errData = await response.json(); errorMsg = errData.detail || errorMsg; } catch {}
        throw new Error(errorMsg);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'property_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      alert(error.message || 'Failed to download template');
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/import/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Preview failed');
      setImportPreview(data);
    } catch (error) {
      alert(error.message || 'Failed to preview file');
      setImportFile(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('update_duplicates', 'true');
      const overrides = {};
      Object.entries(manualMappings).forEach(([col, field]) => {
        if (field) overrides[col] = field;
      });
      formData.append('field_mappings', JSON.stringify(overrides));
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/import/auto`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Import failed');
      setImportResult(result);
      loadProperties();
    } catch (error) {
      alert(error.message || 'Failed to import properties');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportResult(null);
    setManualMappings({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-6" data-testid="properties-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-200">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Building2 className="text-teal-600" size={24} />
            Properties
          </h2>
          <p className="text-sm text-gray-500 mt-1">Manage hotel properties</p>
        </div>
        <div className="flex gap-2">
          {canImportExport && (
            <button 
              onClick={() => setShowImportExport(true)}
              className="flex items-center px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              data-testid="import-export-btn"
            >
              <FileSpreadsheet size={18} className="mr-2" /> Import / Export
            </button>
          )}
          {canEditProperty && (
            <button 
              onClick={() => window.open('/properties/new', '_blank')}
              className="flex items-center px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm font-medium"
              data-testid="add-property-btn"
            >
              <Plus size={20} className="mr-2" /> Add Property
            </button>
          )}
          {isGtmSales && (
            <span className="px-3 py-2 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-testid="properties-readonly-badge">
              View-only access
            </span>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <SummaryCard 
          title="Total Properties" 
          count={summaryStats.total} 
          icon={Building2}
          color="blue"
          onClick={() => { setStatusFilter('all'); setAdvancedFilters({}); }}
          isActive={statusFilter === 'all' && advancedFilterCount === 0}
        />
        <SummaryCard 
          title="Active" 
          count={summaryStats.active} 
          icon={CheckCircle}
          color="green"
          onClick={() => setStatusFilter('active')}
          isActive={statusFilter === 'active'}
        />
        <SummaryCard 
          title="Inactive" 
          count={summaryStats.inactive} 
          icon={Power}
          color="gray"
          onClick={() => setStatusFilter('inactive')}
          isActive={statusFilter === 'inactive'}
        />
      </div>

      {/* Search and Filters Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by property name..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
              data-testid="property-search"
            />
          </div>
          
          <select
            value={grbMappingFilter}
            onChange={(e) => setGrbMappingFilter(e.target.value)}
            className={`px-4 py-2.5 border rounded-lg transition-colors font-medium bg-white outline-none focus:ring-2 focus:ring-teal-500 ${
              grbMappingFilter !== 'all' ? 'bg-teal-50 border-teal-300 text-teal-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
            title="GRB Mapping"
            data-testid="grb-mapping-filter"
          >
            <option value="all">GRB Mapping: All</option>
            <option value="mapped">Mapped</option>
            <option value="not_mapped">Not Mapped</option>
          </select>
          
          <button
            onClick={() => setShowAdvancedFilter(true)}
            className={`flex items-center px-4 py-2.5 border rounded-lg transition-colors font-medium relative ${
              advancedFilterCount > 0 ? 'bg-teal-50 border-teal-300 text-teal-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="open-advanced-filters"
          >
            <SlidersHorizontal size={18} className="mr-2" />
            Advanced Filters
            {advancedFilterCount > 0 && (
              <span className="ml-2 bg-teal-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="filter-count-badge">
                {advancedFilterCount}
              </span>
            )}
          </button>
          
          <button
            onClick={() => { loadProperties(); }}
            className="flex items-center px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            title="Refresh"
            data-testid="refresh-properties-btn"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Active Filter Tags */}
        {activeFilterTags.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2" data-testid="active-filter-tags">
            <span className="text-xs text-gray-500 font-medium">Active filters:</span>
            {activeFilterTags.map(tag => (
              <FilterTag
                key={tag.key}
                label={tag.label}
                value={tag.value}
                onRemove={() => handleRemoveTag(tag)}
              />
            ))}
            <button
              onClick={() => { setAdvancedFilters({}); setSearchQuery(''); setStatusFilter('all'); }}
              className="text-xs text-red-500 hover:text-red-700 underline ml-2"
              data-testid="clear-all-filters"
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Room Inventory Charts — disabled, component preserved for future re-enable */}

      {/* Pagination Controls */}
      <div className="flex justify-between items-center bg-white p-2.5 rounded-lg border border-gray-200 shadow-sm" data-testid="properties-pagination-controls">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 pl-2">Show</span>
          <select
            className="border border-gray-300 rounded-md text-sm p-1 focus:ring-teal-500 focus:border-teal-500 outline-none"
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(e.target.value === 'all' ? 'all' : Number(e.target.value)); setCurrentPage(1); }}
            data-testid="properties-items-per-page"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={75}>75</option>
            <option value={100}>100</option>
            <option value="all">All</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
          <span className="text-xs text-gray-400 ml-2">({totalProperties} total)</span>
        </div>
        {itemsPerPage !== 'all' && totalPages > 1 && (
          <div className="flex items-center gap-4">
            <div className="flex items-center border rounded-md overflow-hidden">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-2 border-r hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="properties-page-first">
                <ChevronsLeft size={16} />
              </button>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 border-r hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="properties-page-prev">
                <ChevronLeft size={16} />
              </button>
              <span className="px-4 py-1 text-sm font-medium text-gray-700 bg-gray-50" data-testid="properties-page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 border-l hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="properties-page-next">
                <ChevronRight size={16} />
              </button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-2 border-l hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="properties-page-last">
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Delete Toolbar */}
      {isAdmin && bulkDeleteEnabled && (
        <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-2 border border-gray-200" data-testid="properties-bulk-delete-toolbar">
          <input
            type="checkbox"
            checked={selectedPropertyIds.length === properties.length && properties.length > 0}
            onChange={(e) => toggleAllProperties(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            data-testid="select-all-properties"
          />
          <span className="text-sm text-gray-600">
            {selectedPropertyIds.length > 0 ? `${selectedPropertyIds.length} selected` : 'Select all'}
          </span>
          {selectedPropertyIds.length > 0 && (
            <button
              onClick={handleBulkDeleteProperties}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
              data-testid="delete-selected-properties-btn"
            >
              <Trash2 size={14} /> Delete Selected ({selectedPropertyIds.length})
            </button>
          )}
        </div>
      )}

      {/* Property Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      ) : properties.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="properties-grid">
          {properties.map(property => (
            <div key={property.id} className="relative">
              {isAdmin && bulkDeleteEnabled && (
                <div className="absolute top-3 left-3 z-10">
                  <input
                    type="checkbox"
                    checked={selectedPropertyIds.includes(property.id)}
                    onChange={(e) => togglePropertySelect(property.id, e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    data-testid={`select-property-${property.id}`}
                  />
                </div>
              )}
              <PropertyCard
                property={property}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={(p) => setDeleteModal({ isOpen: true, property: p })}
                onToggleStatus={handleToggleStatus}
                onMapGRB={setGrbMapTarget}
                canDelete={canDeleteProperty}
                canEdit={canEditProperty}
                canMapGRB={canMapGRB}
                isToggling={togglingStatus}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No properties found</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery || statusFilter !== 'all' || advancedFilterCount > 0
              ? 'Try adjusting your search or filters'
              : 'Add your first property to get started'
            }
          </p>
          {!searchQuery && statusFilter === 'all' && advancedFilterCount === 0 && (
            <button
              onClick={() => navigate('/properties/new')}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
            >
              Add Your First Property
            </button>
          )}
        </div>
      )}

      {/* Pagination Footer */}
      {!loading && totalProperties > 0 && (
        <div className="px-4 py-2.5 bg-white rounded-lg border border-gray-200 text-xs text-gray-500 flex justify-between" data-testid="properties-pagination-footer">
          <span>
            Showing {itemsPerPage === 'all' ? 1 : (currentPage - 1) * itemsPerPage + 1} to {itemsPerPage === 'all' ? totalProperties : Math.min(currentPage * itemsPerPage, totalProperties)} of {totalProperties} properties
          </span>
          <span>Total Properties: {summaryStats.total}</span>
        </div>
      )}

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, property: null })}
        onConfirm={handleDelete}
        propertyName={deleteModal.property?.propertyName}
      />

      {/* Phase 19.5 — Bulk Delete Properties confirmation (Admin only). */}
      {showBulkDeletePropsModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => !bulkDeletingProps && setShowBulkDeletePropsModal(false)}
          data-testid="properties-bulk-delete-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
              <Trash2 size={20} className="text-red-600" /> Delete Properties
            </h2>
            <p className="text-gray-600 text-sm">
              Are you sure you want to permanently delete the <strong>{selectedPropertyIds.length}</strong> selected properties?
            </p>
            <p className="text-rose-600 text-xs mt-2 font-medium">This action cannot be undone.</p>
            <div className="flex gap-3 pt-5">
              <button
                type="button"
                disabled={bulkDeletingProps}
                onClick={() => setShowBulkDeletePropsModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium"
                data-testid="properties-bulk-delete-cancel"
              >Cancel</button>
              <button
                type="button"
                disabled={bulkDeletingProps}
                onClick={confirmBulkDeleteProperties}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="properties-bulk-delete-confirm"
              >
                {bulkDeletingProps ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Filter Sidebar */}
      <PropertyAdvancedFilter
        isOpen={showAdvancedFilter}
        onClose={() => setShowAdvancedFilter(false)}
        onApply={handleAdvancedFilterApply}
        filterOptions={filterOptions}
      />

      {/* Import/Export Modal */}
      {showImportExport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <FileSpreadsheet size={20} className="text-teal-600" />
                Import / Export Properties
              </h3>
              <button onClick={() => { setShowImportExport(false); resetImport(); }} data-testid="close-import-export-modal">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
              {/* Export Section */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-1 flex items-center gap-2">
                  <Download size={18} className="text-blue-600" />
                  Export Properties
                </h4>
                <p className="text-xs text-gray-500 mb-3">Export all properties using the master template format.</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleExport('excel')}
                    disabled={exporting}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
                    data-testid="export-excel-btn"
                  >
                    {exporting ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
                    Export Excel
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    disabled={exporting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
                    data-testid="export-csv-btn"
                  >
                    {exporting ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
                    Export CSV
                  </button>
                </div>
              </div>
              
              {/* Import Section */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-1 flex items-center gap-2">
                  <Upload size={18} className="text-teal-600" />
                  Import Properties
                </h4>
                <p className="text-xs text-gray-500 mb-3">
                  Upload an Excel/CSV file using the master template.
                </p>

                <button
                  onClick={handleDownloadTemplate}
                  disabled={exporting}
                  className="mb-4 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="download-template-btn"
                >
                  <Download size={14} />
                  Download Blank Template
                </button>
                
                {importResult ? (
                  <div data-testid="import-results">
                    <div className="p-4 bg-white rounded-lg border border-gray-200 space-y-3">
                      <h5 className="font-medium text-gray-800">Import Complete</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-green-700">{importResult.created}</div>
                          <div className="text-xs text-green-600">Created</div>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-blue-700">{importResult.updated}</div>
                          <div className="text-xs text-blue-600">Updated</div>
                        </div>
                        <div className="p-3 bg-gray-100 rounded-lg text-center">
                          <div className="text-2xl font-bold text-gray-700">{importResult.skipped}</div>
                          <div className="text-xs text-gray-600">Skipped</div>
                        </div>
                        <div className="p-3 bg-red-50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-red-700">{importResult.failed}</div>
                          <div className="text-xs text-red-600">Failed</div>
                        </div>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div className="mt-2">
                          <h6 className="text-xs font-medium text-red-700 mb-1">Errors:</h6>
                          <div className="max-h-32 overflow-y-auto text-xs text-red-600 space-y-1">
                            {importResult.errors.map((err, i) => (
                              <p key={i}>Row {err.row}: {err.reason}</p>
                            ))}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={resetImport}
                        className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                        data-testid="import-another-btn"
                      >
                        Import Another File
                      </button>
                    </div>
                  </div>
                ) : !importPreview ? (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-500 w-full text-center transition-colors"
                      data-testid="file-upload-btn"
                    >
                      <Upload size={24} className="mx-auto mb-2 text-gray-400" />
                      <span className="text-gray-600 text-sm">Click to select CSV or Excel file</span>
                    </button>
                  </div>
                ) : (
                  <div data-testid="import-preview-section">
                    <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600">
                        File: <strong>{importFile?.name}</strong>
                      </p>
                      <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                        <span>{importPreview.total_rows} total rows</span>
                        <span className="text-teal-600 font-medium">{importPreview.mapped_columns || 0} auto-mapped</span>
                        {importPreview.unmapped_columns?.length > 0 && (
                          <span className="text-amber-600 font-medium">{importPreview.unmapped_columns.length} unmapped</span>
                        )}
                        {importPreview.property_count > 0 && <span>{importPreview.property_count} properties detected</span>}
                        {importPreview.additional_room_rows > 0 && <span>{importPreview.additional_room_rows} additional room rows</span>}
                      </div>
                    </div>
                    
                    {importPreview.suggested_mapping && (
                      <div className="mb-4">
                        <h5 className="text-xs font-medium text-gray-700 mb-2">Auto-Mapped Columns</h5>
                        <div className="max-h-40 overflow-y-auto space-y-1 p-2 bg-white rounded-lg border border-gray-200">
                          {Object.entries(importPreview.suggested_mapping)
                            .filter(([, field]) => field)
                            .map(([col, field]) => (
                            <div key={col} className="flex items-center gap-2 text-xs py-0.5">
                              <span className="w-2/5 truncate text-gray-600 font-medium">{col}</span>
                              <span className="text-gray-300">-</span>
                              <span className="flex-1 text-teal-700 bg-teal-50 px-2 py-0.5 rounded">{field}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {importPreview.unmapped_columns?.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-xs font-medium text-amber-700 mb-2">Unmapped Columns</h5>
                        <div className="max-h-48 overflow-y-auto space-y-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                          {importPreview.unmapped_columns.map((col) => (
                            <div key={col} className="flex items-center gap-2 text-xs">
                              <span className="w-2/5 truncate text-gray-700 font-medium">{col}</span>
                              <span className="text-gray-300">-</span>
                              <select
                                value={manualMappings[col] || ''}
                                onChange={(e) => setManualMappings(prev => ({ ...prev, [col]: e.target.value }))}
                                className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:ring-teal-500 focus:border-teal-500 outline-none"
                                data-testid={`manual-map-${col}`}
                              >
                                <option value="">-- Skip --</option>
                                <optgroup label="Property Fields">
                                  {importPreview.available_fields
                                    ?.filter(f => !f.startsWith('_') && !f.startsWith('amenities.') && !f.startsWith('facilities.') && !f.startsWith('bankDetails.') && !f.startsWith('documents.'))
                                    .map(f => <option key={f} value={f}>{f}</option>)}
                                </optgroup>
                                <optgroup label="Amenities">
                                  {importPreview.available_fields
                                    ?.filter(f => f.startsWith('amenities.'))
                                    .map(f => <option key={f} value={f}>{f.replace('amenities.', '')}</option>)}
                                </optgroup>
                                <optgroup label="Facilities">
                                  {importPreview.available_fields
                                    ?.filter(f => f.startsWith('facilities.'))
                                    .map(f => <option key={f} value={f}>{f.replace('facilities.', '')}</option>)}
                                </optgroup>
                                <optgroup label="Bank Details">
                                  {importPreview.available_fields
                                    ?.filter(f => f.startsWith('bankDetails.'))
                                    .map(f => <option key={f} value={f}>{f.replace('bankDetails.', '')}</option>)}
                                </optgroup>
                                <optgroup label="Room Fields">
                                  {importPreview.available_fields
                                    ?.filter(f => f.startsWith('_'))
                                    .map(f => <option key={f} value={f}>{f.replace('_', '').replace(/_/g, ' ')}</option>)}
                                </optgroup>
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <button
                        onClick={resetImport}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                        data-testid="cancel-import-btn"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleImport}
                        disabled={importing}
                        className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium"
                        data-testid="confirm-import-btn"
                      >
                        {importing ? (
                          <>
                            <Loader2 className="animate-spin" size={16} />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Upload size={16} />
                            Import Properties
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {grbMapTarget && (
        <GRBMapModal
          property={grbMapTarget}
          onClose={() => setGrbMapTarget(null)}
          onSaved={() => loadProperties()}
        />
      )}
    </div>
  );
};

export default Properties;
